import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import { Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import cn from 'classnames'
import moment from 'moment'
import React from 'react'
import Loader from '../../../common/loader/Loader'
import { MarketSalesAmountAndVolumes } from '../../../common/marketSales/types'
import { formatNumber } from '../../../common/monthlyWeeklyInfo/marketPageUtil'
import { ChainMarketSettings } from '../../../common/types/chainMarketSettings'
import { Query } from '../../../react-apollo/Components'
import { i18n } from '../../index/index'
import RequiresAction from '../RequiresAction'
import css from '../weeklyInfoPage.css'
import { fetchWeeklyInfoMarketSalesAndProfit } from '../weeklyInfoQueries'

interface Props {
  date: string
  stationId: string
}

interface QueryResponse {
  weeklyInfoMarketSalesAndProfit?: MarketSalesAmountAndVolumes[]
  chainMarketSettings?: ChainMarketSettings[]
}

interface QueryVariables {
  date: string
  stationId: string
}

export default function MarketSalesAndGrossProfit({ date, stationId }: Props) {
  return (
    <Query<QueryResponse, QueryVariables> query={fetchWeeklyInfoMarketSalesAndProfit} variables={{ date, stationId }}>
      {({ data, error, loading }) => {
        let { weeklyInfoMarketSalesAndProfit } = data
        const { chainMarketSettings } = data || {}
        const salesVolumeTarget = chainMarketSettings?.find(
          (setting) =>
            setting.name === 'MARKET_SALES_VOLUME_TARGET' &&
            moment(setting.startDate).get('year') === moment(date).get('year'),
        )

        let salesVolumeTargetValue = 0
        if (salesVolumeTarget && salesVolumeTarget.value && !isNaN(salesVolumeTarget.value)) {
          salesVolumeTargetValue = Number(salesVolumeTarget.value)
        }
        if (!weeklyInfoMarketSalesAndProfit) {
          weeklyInfoMarketSalesAndProfit = []
        }
        return (
          <Loader
            {...{
              loading,
              error,
            }}
          >
            <div className={css.salesAndHours}>
              <Typography variant='h6' gutterBottom>
                {i18n.t('weeklyInfo.title.marketSales', {
                  week: `vko. ${moment(date).isoWeek()} ${moment(date).startOf('week').format('D.M.')}-${moment(date)
                    .endOf('week')
                    .format('D.M.')}`,
                })}
              </Typography>
              <Paper>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell className={cn(css.tableHeading, css.arrowCell)} />
                      <TableCell className={css.tableHeading}>
                        {i18n.t('weeklyInfo.heading.productGroupName')}
                      </TableCell>
                      <TableCell className={css.tableHeading}>
                        {i18n.t('weeklyInfo.heading.salesAmountLastWeek')}
                      </TableCell>
                      <TableCell className={css.tableHeading}>
                        {i18n.t('weeklyInfo.heading.salesAmountLastYear')}
                      </TableCell>
                      <TableCell className={css.tableHeading}>{i18n.t('weeklyInfo.heading.index')}</TableCell>
                      <TableCell className={css.tableHeading}>
                        {i18n.t('weeklyInfo.heading.differenceToGoal')}
                      </TableCell>
                      <TableCell className={css.tableHeading}>
                        {i18n.t('weeklyInfo.heading.salesVolumeLastWeek')}
                      </TableCell>
                      <TableCell className={css.tableHeading}>
                        {i18n.t('weeklyInfo.heading.salesVolumeLastYear')}
                      </TableCell>
                      <TableCell className={css.tableHeading}>{i18n.t('weeklyInfo.heading.salesVolumeGoal')}</TableCell>
                      <TableCell className={css.tableHeading}>
                        {i18n.t('weeklyInfo.heading.isActionRequired')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {weeklyInfoMarketSalesAndProfit.map((row: MarketSalesAmountAndVolumes) => (
                      <MarketSalesAndGrossProfitTableRow
                        key={row.id}
                        row={row}
                        stationId={stationId}
                        date={date}
                        salesVolumeTargetFactorPercentage={salesVolumeTargetValue}
                      />
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </div>
          </Loader>
        )
      }}
    </Query>
  )
}

function MarketSalesAndGrossProfitTableRow({
  row,
  stationId,
  date,
  salesVolumeTargetFactorPercentage,
}: {
  row: MarketSalesAmountAndVolumes
  stationId: string
  date: string
  salesVolumeTargetFactorPercentage: number
}) {
  // Formatted index
  const expandable = row.childVolumesAndSums && row.childVolumesAndSums?.length > 0
  const index =
    row.salesAmountTaxfreeLastYear === 0 ? 100 : (row.salesAmountTaxfree / row.salesAmountTaxfreeLastYear) * 100
  const salesVolumeTargetFactor = salesVolumeTargetFactorPercentage / 100.0
  const salesVolumeTarget = Math.round(row.salesVolumeLastYear * salesVolumeTargetFactor)
  const differenceToGoal = row.salesVolume - salesVolumeTarget
  const [isExpanded, setExpanded] = React.useState(false)

  function toggleExpanded() {
    setExpanded(!isExpanded)
  }

  return (
    <>
      <TableRow>
        <TableCell className={cn(css.tableCell, css.arrowCell, css.pointer)}>
          {expandable && (
            <a className={cn(css.arrow, { [css.open]: isExpanded })} onClick={toggleExpanded}>
              <KeyboardArrowRightIcon />
            </a>
          )}
        </TableCell>
        <TableCell className={css.tableCell}>{row.cdtName ?? row.cdtId}</TableCell>
        <TableCell className={css.tableCell}>{formatNumber(row.salesAmountTaxfree)}</TableCell>
        <TableCell className={css.tableCell}>{formatNumber(row.salesAmountTaxfreeLastYear)}</TableCell>
        <ColoredCell value={index} threshold={100.0} />
        <ColoredCell value={differenceToGoal} />
        <TableCell className={css.tableCell}>{formatNumber(row.salesVolume)}</TableCell>
        <TableCell className={css.tableCell}>{formatNumber(row.salesVolumeLastYear)}</TableCell>
        <TableCell className={css.tableCell}>{formatNumber(salesVolumeTarget)}</TableCell>
        <TableCell className={cn(css.tableCell, css.actionCell)}>
          <RequiresAction
            date={date}
            isActionRequired={row.isActionRequired}
            weeklyInfoId={row.id}
            stationId={stationId}
            subject='Management'
          />
        </TableCell>
      </TableRow>
      {isExpanded &&
        row.childVolumesAndSums?.map((childRow) => (
          <MarketSalesAndGrossProfitTableRow
            key={childRow.id}
            row={childRow}
            stationId={stationId}
            date={date}
            salesVolumeTargetFactorPercentage={salesVolumeTargetFactorPercentage}
          />
        ))}
    </>
  )
}

function ColoredCell({ value, threshold = 0 }: { value: number; threshold?: number }) {
  const colorStyle = value < threshold ? css.negativeCell : css.positiveCell
  return <TableCell className={cn(css.tableCell, colorStyle)}>{formatNumber(value)}</TableCell>
}
